import type { ValidationError } from 'yup'
import type {
    ActionEndedEventDetail,
    ActionFailedEventDetail,
    ActionSuccessEventDetail,
    FailedPartialValidationEventDetail,
    PassedPartialValidationEventDetail,
    RequestValidationEventDetail,
    SubmitFailedEventDetail,
    SubmitSuccessEventDetail,
} from '../types/EventDetailTypes'
import type { HandledApplicationError } from '../errors/HandledApplicationError'
import {
    ACTION_ENDED_EVENT,
    ACTION_FAILED_EVENT,
    ACTION_SUCCESS_EVENT,
    FAILED_PARTIAL_VALIDATION_EVENT,
    PASSED_PARTIAL_VALIDATION_EVENT,
    REQUEST_PARTIAL_VALIDATION_EVENT,
    SUBMIT_FAILED_EVENT,
    SUBMIT_SUCCESS_EVENT,
} from '../constants/custom-event-names'
import { TypeOfValidations } from '../constants/validation-constants'

/**
 * @event SubmitFailed
 * */
export const setupSubmitFailedCustomEvent = (formId: string, validationErrors: ValidationError[]) => {
    return new CustomEvent<SubmitFailedEventDetail>(SUBMIT_FAILED_EVENT, {
        detail: { formId, validationErrors },
        bubbles: true,
    })
}

/**
 * @event SubmitSuccess
 * */
export const setupSubmitSuccessCustomEvent = (formId: string) => {
    return new CustomEvent<SubmitSuccessEventDetail>(SUBMIT_SUCCESS_EVENT, { detail: { formId }, bubbles: true })
}

/**
 * @event ActionSuccess
 * */
export const setupActionSuccessEvent = <TState extends any = {}>(formId: string, newState?: TState) => {
    return new CustomEvent<ActionSuccessEventDetail<TState>>(ACTION_SUCCESS_EVENT, {
        detail: { formId, newState },
        bubbles: true,
    })
}

/**
 * @event ActionFailed
 * */
export const setupActionFailedEvent = (formId: string, error: HandledApplicationError) => {
    return new CustomEvent<ActionFailedEventDetail>(ACTION_FAILED_EVENT, { detail: { formId, error }, bubbles: true })
}

/**
 * @event ActionEnded
 * */
export const setupActionEndedEvent = (formId: string) => {
    return new CustomEvent<ActionEndedEventDetail>(ACTION_ENDED_EVENT, { detail: { formId }, bubbles: false })
}

/**
 * @event PassedPartialValidation
 * */
export const setupFailedPartialValidationEvent = (
    form: HTMLFormElement,
    errors: ValidationError[],
    validatedFields: string[],
) => {
    return new CustomEvent<FailedPartialValidationEventDetail>(FAILED_PARTIAL_VALIDATION_EVENT, {
        detail: { formId: form.id, validationErrors: errors, validatedFields },
        bubbles: true,
    })
}

/**
 * @event PassedPartialValidation
 * */
export const setupPassedPartialValidationEvent = (form: HTMLFormElement, validatedFields: string[]) => {
    return new CustomEvent<PassedPartialValidationEventDetail>(PASSED_PARTIAL_VALIDATION_EVENT, {
        detail: { formId: form.id, validatedFields },
        bubbles: true,
    })
}

/**
 * @event RequestPartialValidation
 * */
export const setupRequestPartialValidationEvent = (
    fieldName: string,
    typeOfValidation: TypeOfValidations,
    form: HTMLFormElement | null,
    hasCustomValidation?: boolean,
) => {
    const detail: RequestValidationEventDetail = {
        fieldName: fieldName,
        formId: form?.id,
        typeOfValidation,
        hasCustomValidation,
    }
    return new CustomEvent(REQUEST_PARTIAL_VALIDATION_EVENT, { detail, bubbles: true })
}
